import React from 'react';
import { useTranslation } from 'react-i18next';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { TimePicker } from '@/components/ui/inputs/TimePicker';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useManualAttendanceForm } from '@/hooks/attendance';
import { SidePanelFormLayout } from '@/components/layout';


const NOTES_MAX_LENGTH = 100;
const NOTES_TEXTAREA_ROWS = 3;

interface ManualAttendanceFormProps {
  onSubmit?: () => void;
  readOnly?: boolean;
}

const ManualAttendanceForm: React.FC<ManualAttendanceFormProps> = ({
  onSubmit,
  readOnly,
}) => {
  const { t: translate } = useTranslation();
  
  const {
    employeeId,
    locationId,
    attendanceDate,
    attendanceTime,
    notes,
    maxDate,
    handleEmployeeChange,
    handleLocationChange,
    handleDateChange,
    handleTimeChange,
    handleNotesChange,
    employeeOptions,
    locationOptions,
    employeeHasMore,
    loadMoreEmployees,
    handleEmployeeSearch,
    isLoadingEmployees,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    handleSubmit,
    errors,
    isSubmitted,
  } = useManualAttendanceForm({ onSubmit, readOnly });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="manual-attendance-form">
      <SidePanelFormLayout.Fields>
        <Selector
          label={translate('attendance:manual.fields.employee')}
          placeholder={translate('attendance:manual.placeholders.selectEmployee')}
          options={employeeOptions}
          value={employeeId}
          onChange={handleEmployeeChange}
          searchable={true}
          onSearchChange={handleEmployeeSearch}
          hasNextPage={employeeHasMore}
          onLoadMore={loadMoreEmployees}
          isLoading={isLoadingEmployees}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.employeeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors?.employeeId && isSubmitted
              ? translate(errors.employeeId.message as string)
              : undefined
          }
          required
        />

        <DatePicker
          label={translate('attendance:manual.fields.date')}
          value={attendanceDate}
          onChange={handleDateChange}
          maxDate={maxDate}
          color={errors.attendanceDate ? 'error' : undefined}
          supportingText={
            errors.attendanceDate?.message
              ? translate(errors.attendanceDate.message)
              : undefined
          }
          required
        />

        <TimePicker
          label={translate('attendance:manual.fields.time')}
          value={attendanceTime}
          onChange={handleTimeChange}
          disabled={effectiveReadOnly || isSubmitting}
          placeholder={translate('attendance:manual.placeholders.enterTime')}
          color={errors.attendanceTime ? 'error' : undefined}
          supportingText={
            errors.attendanceTime?.message
              ? translate(errors.attendanceTime.message)
              : undefined
          }
          required
        />

        <Selector
          label={translate('attendance:manual.fields.location')}
          placeholder={translate('attendance:manual.placeholders.selectLocation')}
          options={locationOptions}
          value={locationId}
          onChange={handleLocationChange}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.locationId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors?.locationId && isSubmitted
              ? translate(errors.locationId.message as string)
              : undefined
          }
          required
        />

        <TextArea
          label={translate('attendance:manual.fields.notes')}
          placeholder={translate('attendance:manual.placeholders.enterNotes')}
          value={notes}
          onChange={handleNotesChange}
          disabled={effectiveReadOnly || isSubmitting}
          maxLength={NOTES_MAX_LENGTH}
          required
          rows={NOTES_TEXTAREA_ROWS}
          color={errors.notes && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.notes && isSubmitted
              ? translate(errors.notes.message as string)
              : undefined
          }
          showCharacterCount
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={!isReady || isSubmitting}
          >
            {translate('attendance:manual.actions.register')}
          </PrimaryButton>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default ManualAttendanceForm;
